import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBillingStore } from '@/stores/billingStore';

export const CreditsPage = () => {
  const navigate = useNavigate();
  const { creditPacks, creditsBalance, isLoading, error, fetchCreditPacks, fetchBillingStatus, addToCart } = useBillingStore();

  useEffect(() => {
    fetchCreditPacks();
    fetchBillingStatus();
  }, [fetchCreditPacks, fetchBillingStatus]);

  // Fallback static packs
  const packs = creditPacks.length > 0 ? creditPacks : [
    { productId: 4, name: '50 Credits', credits: 50, price: 4.90 },
    { productId: 4, name: '200 Credits', credits: 200, price: 14.90 },
    { productId: 4, name: '500 Credits', credits: 500, price: 29.90 },
  ];

  const handleBuyCredits = async (pack: typeof packs[0]) => {
    const success = await addToCart(5, pack.productId); // planId 5 = Monthly
    if (success) {
      navigate('/billing/checkout');
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-2">Buy Credits</h1>
      <p className="text-slate-500 dark:text-slate-400 mb-2">
        Credits are used for on-demand synchronization and high-frequency sync operations
      </p>
      <p className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-8">
        Current balance: <span className="text-primary">{creditsBalance} credits</span>
      </p>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {packs.map((pack, idx) => {
          const isPopular = idx === 1;
          const pricePerCredit = (pack.price / pack.credits).toFixed(3);

          return (
            <div
              key={idx}
              className={`relative bg-white dark:bg-slate-800 rounded-xl border-2 p-6 ${
                isPopular
                  ? 'border-primary shadow-lg'
                  : 'border-slate-200 dark:border-slate-700'
              }`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-white text-xs font-semibold rounded-full">
                  Popular
                </div>
              )}

              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{pack.name}</h3>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-3xl font-bold text-slate-800 dark:text-slate-200">
                  {pack.price.toFixed(2)}
                </span>
                <span className="text-slate-500 dark:text-slate-400">EUR</span>
              </div>

              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {pricePerCredit}/credit
              </p>

              <button
                onClick={() => handleBuyCredits(pack)}
                disabled={isLoading}
                className={`mt-6 w-full py-2.5 px-4 rounded-lg font-medium transition-colors ${
                  isPopular
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600'
                }`}
              >
                {isLoading ? 'Processing...' : 'Buy'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Credit usage info */}
      <div className="mt-8 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3">How credits are used</h3>
        <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
          <li className="flex items-center justify-between">
            <span>Manual sync (single account)</span>
            <span className="font-medium">1 credit</span>
          </li>
          <li className="flex items-center justify-between">
            <span>Sync all accounts</span>
            <span className="font-medium">1 credit/account</span>
          </li>
          <li className="flex items-center justify-between">
            <span>Historical fetch (&gt;90 days)</span>
            <span className="font-medium">2 credits</span>
          </li>
          <li className="flex items-center justify-between">
            <span>Export to CSV/PDF</span>
            <span className="font-medium text-green-600 dark:text-green-400">Free</span>
          </li>
        </ul>
      </div>
    </div>
  );
};
